import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { XIcon, CheckIcon, ClockIcon, ZapIcon, MessageSquareIcon, MapIcon, ClipboardCheckIcon, ShieldIcon, ActivityIcon, DownloadIcon } from 'lucide-react';
import { HeartbeatDivider } from '../components/HeartbeatDivider';
import { FAQAccordion } from '../components/FAQAccordion';
import { LegalModal } from '../components/LegalModal';
// Shared animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function ProtocoloPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  return <div className="min-h-screen bg-deep text-text-primary overflow-x-hidden selection:bg-teal/30 selection:text-teal" data-id="element-23">
      {/* 1. HERO SECTION */}
      <section className="relative pt-20 pb-16 md:pt-32 md:pb-24 px-6 max-w-7xl mx-auto" data-id="element-24">
        {/* Subtle background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[500px] bg-teal-glow rounded-full blur-[120px] opacity-50 pointer-events-none" data-id="element-25" />

        <div className="relative z-10 grid md:grid-cols-2 gap-12 items-center" data-id="element-26">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-6" data-id="element-27">
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight" data-id="element-28">
              Protocolo Post-Crisis: <br data-id="element-29" />
              <span className="text-text-secondary text-3xl md:text-4xl lg:text-5xl mt-2 block font-medium" data-id="element-30">
                Recupera tu funcionalidad en 24 horas cuando la depresión
                detiene tu mundo.
              </span>
            </motion.h1>
            <motion.h2 variants={fadeInUp} className="text-lg md:text-xl text-text-muted leading-relaxed max-w-xl" data-id="element-31">
              La hoja de ruta exacta para salir del "parón emocional", cumplir
              con tus obligaciones y silenciar la culpa sin necesidad de "tener
              fuerza de voluntad".
            </motion.h2>
            <motion.div variants={fadeInUp} data-id="element-32">
              <a href="#descargar" className="inline-flex items-center gap-3 px-10 py-5 bg-teal text-bg-deep font-heading font-bold text-xl md:text-2xl rounded-xl animate-pulse-teal shadow-[0_0_30px_rgba(79,209,197,0.3)] hover:shadow-[0_0_40px_rgba(79,209,197,0.5)] hover:scale-[1.03] active:scale-[0.98] transition-all duration-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-teal/50" data-id="element-33">
                <DownloadIcon className="w-7 h-7" data-id="element-34" />
                Descargar ahora
              </a>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-35">
            <div className="absolute inset-0 bg-teal/20 blur-3xl rounded-full transform scale-90" data-id="element-36" />
            <img src="https://cdn.magicpatterns.com/uploads/53mJPZvgUp5ot1EoXVXJtL/Protocolo_Post_Crisis.png" alt="Protocolo Post-Crisis eBook on a tablet" className="relative z-10 w-full max-w-md mx-auto rounded-xl shadow-2xl shadow-black/50 border border-white/5" data-id="element-37" />
          </motion.div>
        </div>
      </section>

      <HeartbeatDivider data-id="element-38" />

      {/* 2. OPENING HOOK */}
      <section className="py-16 px-6" data-id="element-39">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeInUp} className="max-w-3xl mx-auto space-y-6 text-lg md:text-xl text-text-secondary leading-relaxed" data-id="element-40">
          <p className="text-3xl md:text-4xl font-heading font-bold text-teal mb-8" data-id="element-41">
            Son las 3:00 PM.
          </p>
          <p data-id="element-42">
            Estás en la cama o encerrado en el baño. El pecho te pesa, tienes 20
            mensajes sin responder y una lista de tareas que parece una montaña
            imposible.
          </p>
          <p data-id="element-43">Sientes que has fallado otra vez.</p>
          <p data-id="element-44">
            La depresión no te ha quitado la capacidad de querer cumplir; te ha
            quitado el acceso a la energía para hacerlo.
          </p>
          <div className="p-6 mt-8 border-l-4 border-amber-warm bg-amber-warm/5 rounded-r-lg" data-id="element-45">
            <p className="text-amber-warm font-medium" data-id="element-46">
              Este no es un momento para "pensar positivo". Es un momento de
              emergencia biológica.
            </p>
          </div>
        </motion.div>
      </section>

      {/* 3. THE PROBLEM */}
      <section className="py-16 px-6" data-id="element-47">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeInUp} className="max-w-3xl mx-auto bg-card border-l-4 border-teal p-8 md:p-10 rounded-r-xl shadow-lg" data-id="element-48">
          <h3 className="text-2xl md:text-3xl font-bold text-text-primary mb-6" data-id="element-49">
            El problema no eres tú
          </h3>
          <div className="space-y-4 text-text-secondary" data-id="element-50">
            <p data-id="element-51">El problema no es que seas "vago" o "débil".</p>
            <p data-id="element-52">
              El problema es que el colapso depresivo activa un mecanismo de
              defensa en tu cerebro que apaga tu capacidad de tomar decisiones.
            </p>
            <p data-id="element-53">
              Intentar forzarte a "salir a caminar" o "animarte" cuando estás en
              este estado es como pedirle a alguien con una pierna rota que
              corra un maratón.
            </p>
            <p className="font-medium text-text-primary" data-id="element-54">
              Solo genera más frustración, más agotamiento y una vergüenza
              paralizante porque el mundo sigue girando, pero tú estás detenido.
            </p>
          </div>
        </motion.div>
      </section>

      <HeartbeatDivider data-id="element-55" />

      {/* 4. WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-16 px-6" data-id="element-56">
        <div className="max-w-4xl mx-auto" data-id="element-57">
          <motion.h3 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-2xl md:text-3xl font-bold text-center mb-12" data-id="element-58">
            Por qué lo que intentaste no funcionó
          </motion.h3>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="space-y-6" data-id="element-59">
            {["Los libros de autoayuda te piden 'visualizar el éxito', algo imposible cuando no puedes ni elegir qué desayunar.", 'Los consejos genéricos ignoran la fatiga neuroquímica real que ocurre tras una crisis de llanto o apatía.', 'La terapia es vital a largo plazo, pero no te dice qué escribirle a tu jefe ahora mismo para proteger tu empleo.', "Esperar a 'sentirte motivado' es una trampa, porque la motivación no vuelve hasta que el sistema nervioso se estabiliza."].map((text, i) => <motion.div key={i} variants={fadeInUp} className="flex items-start gap-4 bg-primary p-6 rounded-lg border border-white/5" data-id="element-60">
                <div className="mt-1 bg-red-500/10 p-1 rounded-full flex-shrink-0" data-id="element-61">
                  <XIcon className="w-5 h-5 text-red-400" data-id="element-62" />
                </div>
                <p className="text-text-secondary leading-relaxed" data-id="element-63">{text}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 5. THE UNIQUE MECHANISM (P.A.E.) */}
      <section className="py-20 px-6 bg-card border-y border-white/5 relative overflow-hidden" data-id="element-64">
        <div className="absolute top-0 right-0 w-64 h-64 bg-teal/5 rounded-full blur-3xl" data-id="element-65" />
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-3xl mx-auto text-center relative z-10" data-id="element-66">
          <ActivityIcon className="w-12 h-12 text-teal mx-auto mb-6 opacity-80" data-id="element-67" />
          <h3 className="text-2xl md:text-4xl font-bold mb-6 text-text-primary" data-id="element-68">
            El Sistema de Reactivación por Capas (P.A.E.)
          </h3>
          <p className="text-lg text-text-secondary mb-6 leading-relaxed" data-id="element-69">
            En lugar de intentar "arreglar tu mente", este protocolo utiliza
            micro-choques físicos y estructurales para resetear tu sistema
            nervioso.
          </p>
          <p className="text-lg text-teal-dim font-medium" data-id="element-70">
            Primero estabilizamos tu cuerpo y tu entorno inmediato; la claridad
            mental vendrá después, una vez que hayamos detenido el incendio.
          </p>
        </motion.div>
      </section>

      {/* 6. WHAT YOU GET */}
      <section className="py-20 px-6" data-id="element-71">
        <div className="max-w-5xl mx-auto" data-id="element-72">
          <motion.h3 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-3xl font-bold text-center mb-16" data-id="element-73">
            Lo que recibes
          </motion.h3>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid md:grid-cols-2 gap-6" data-id="element-74">
            {[{
            title: 'El Protocolo de las 4 Horas',
            desc: 'Pasos físicos exactos (incluyendo el reseteo del nervio vago) para detener la rumiación y el llanto.',
            icon: ClockIcon
          }, {
            title: 'La Regla del 1%',
            desc: 'Cómo cumplir con el trabajo y el hogar usando solo el 5% de tu energía habitual.',
            icon: ZapIcon
          }, {
            title: 'Scripts de Comunicación SOS',
            desc: 'Plantillas de texto para copiar y pegar a jefes, pareja o amigos, informando que no estás disponible sin dar explicaciones dolorosas.',
            icon: MessageSquareIcon
          }, {
            title: 'El Mapa de Señales de Humo',
            desc: 'Cómo detectar la próxima caída 48 horas antes de que ocurra para que nunca vuelvas a tocar fondo así.',
            icon: MapIcon
          }, {
            title: 'Checklists de Estabilización',
            desc: 'Una guía visual para cuando tu cerebro no puede procesar párrafos largos.',
            icon: ClipboardCheckIcon
          }].map((item, i) => <motion.div key={i} variants={fadeInUp} className={`bg-card p-8 rounded-xl border-t-2 border-teal/30 hover:border-teal transition-colors duration-300 ${i === 4 ? 'md:col-span-2 md:w-1/2 md:mx-auto' : ''}`} data-id="element-75">
                <item.icon className="w-8 h-8 text-teal mb-4" data-id="element-76" />
                <h4 className="text-xl font-bold text-text-primary mb-2" data-id="element-77">
                  {item.title}
                </h4>
                <p className="text-text-secondary" data-id="element-78">{item.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      <HeartbeatDivider data-id="element-79" />

      {/* 7. WHO THIS IS FOR / NOT FOR */}
      <section className="py-16 px-6" data-id="element-80">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-81">
          {/* For You */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="space-y-6" data-id="element-82">
            <h3 className="text-2xl font-bold text-text-primary flex items-center gap-3" data-id="element-83">
              <CheckIcon className="w-6 h-6 text-teal" data-id="element-84" />
              Esto es para ti si...
            </h3>
            <ul className="space-y-4" data-id="element-85">
              {['Personas con depresión funcional que necesitan seguir trabajando y siendo padres a pesar del vacío.', 'Profesionales 30+ que sienten que su carrera está en riesgo por sus episodios de fatiga mental.', "Quienes sufren de crisis cíclicas y se sienten atrapados en la culpa de 'empezar de cero' cada vez."].map((text, i) => <li key={i} className="flex items-start gap-3 text-text-secondary" data-id="element-86">
                  <div className="mt-1 w-1.5 h-1.5 rounded-full bg-teal flex-shrink-0" data-id="element-87" />
                  <span data-id="element-88">{text}</span>
                </li>)}
            </ul>
          </motion.div>

          {/* Not For You */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="space-y-6 opacity-80" data-id="element-89">
            <h3 className="text-2xl font-bold text-text-muted flex items-center gap-3" data-id="element-90">
              <XIcon className="w-6 h-6 text-text-muted" data-id="element-91" />
              Esto NO es para ti si...
            </h3>
            <ul className="space-y-4" data-id="element-92">
              {['Personas que buscan una cura mágica o instantánea para la depresión clínica.', 'Quienes no están dispuestos a realizar pequeños cambios físicos (como lavarse la cara con agua fría).', 'Casos de emergencia médica inmediata que requieren hospitalización.'].map((text, i) => <li key={i} className="flex items-start gap-3 text-text-muted" data-id="element-93">
                  <div className="mt-1 w-1.5 h-1.5 rounded-full bg-text-muted flex-shrink-0" data-id="element-94" />
                  <span data-id="element-95">{text}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* 8. HOW IT WORKS */}
      <section className="py-20 px-6 bg-primary" data-id="element-96">
        <div className="max-w-5xl mx-auto" data-id="element-97">
          <motion.h3 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-3xl font-bold text-center mb-16" data-id="element-98">
            Cómo funciona
          </motion.h3>

          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-99">
            {/* Desktop connecting line */}
            <div className="hidden md:block absolute top-8 left-1/6 right-1/6 h-0.5 bg-teal-dim/30 z-0" data-id="element-100" />

            {[{
            step: '1',
            title: 'Estabiliza',
            desc: "Aplicas el choque térmico y sensorial para bajar el ritmo cardíaco y salir del 'congelamiento'."
          }, {
            step: '2',
            title: 'Protege',
            desc: 'Usas los guiones de comunicación para blindar tu espacio social y evitar la presión externa.'
          }, {
            step: '3',
            title: 'Ejecuta',
            desc: 'Aplicas el Mínimo Viable para tachar las 2 tareas críticas del día y eliminar la culpa.'
          }].map((item, i) => <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="relative z-10 flex flex-col items-center text-center space-y-4" data-id="element-101">
                <div className="w-16 h-16 rounded-full bg-card border-2 border-teal flex items-center justify-center text-2xl font-bold text-teal shadow-[0_0_15px_rgba(79,209,197,0.2)]" data-id="element-102">
                  {item.step}
                </div>
                <h4 className="text-xl font-bold text-text-primary" data-id="element-103">
                  {item.title}
                </h4>
                <p className="text-text-secondary" data-id="element-104">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 9. PROOF / BELIEVABILITY */}
      <section className="py-20 px-6" data-id="element-105">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-4xl mx-auto bg-card rounded-2xl p-8 md:p-12 border border-teal/20 shadow-[0_0_30px_rgba(79,209,197,0.05)] relative overflow-hidden" data-id="element-106">
          <div className="absolute top-0 right-0 w-32 h-32 bg-teal/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" data-id="element-107" />

          <div className="flex flex-col md:flex-row items-center gap-8 relative z-10" data-id="element-108">
            <div className="flex-shrink-0 flex flex-row md:flex-col gap-6 md:gap-2 items-center md:items-start" data-id="element-109">
              <div className="text-center md:text-left" data-id="element-110">
                <span className="block text-4xl md:text-5xl font-bold text-teal" data-id="element-111">
                  12 min
                </span>
                <span className="text-sm text-text-muted uppercase tracking-wider" data-id="element-112">
                  Tiempo medio
                </span>
              </div>
              <div className="hidden md:block w-full h-px bg-teal-dim/30 my-2" data-id="element-113" />
              <div className="text-center md:text-left" data-id="element-114">
                <span className="block text-4xl md:text-5xl font-bold text-teal" data-id="element-115">
                  15%
                </span>
                <span className="text-sm text-text-muted uppercase tracking-wider" data-id="element-116">
                  Reducción FC
                </span>
              </div>
            </div>

            <div className="flex-1" data-id="element-117">
              <p className="text-lg md:text-xl text-text-secondary leading-relaxed" data-id="element-118">
                En los primeros 12 minutos de aplicar el protocolo de choque
                térmico y rehidratación isotónica, la frecuencia cardíaca media
                baja un 15%, permitiendo que la corteza prefrontal retome el
                control mínimo necesario para ejecutar tareas simples.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      <HeartbeatDivider data-id="element-119" />

      {/* 10. FAQ */}
      <section className="py-16 px-6" data-id="element-120">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} data-id="element-121">
          <h3 className="text-3xl font-bold text-center mb-12" data-id="element-122">
            Preguntas frecuentes
          </h3>
          <FAQAccordion items={[{
          question: '¿Necesito comprar materiales extra?',
          answer: 'No, todo lo que necesitas está en tu casa (agua, hielo, sal, tu teléfono).'
        }, {
          question: '¿Es muy largo de leer?',
          answer: 'No. Está diseñado para un cerebro fatigado: frases cortas, muchas listas y pasos directos.'
        }, {
          question: '¿Sirve si estoy medicado?',
          answer: 'Sí, es un complemento funcional que no interfiere con tratamientos clínicos.'
        }]} data-id="element-123" />
        </motion.div>
      </section>

      {/* 11. OFFER + CTA SECTION */}
      <section id="descargar" className="py-24 px-6 bg-gradient-to-b from-deep to-[#05080a] border-t border-white/5 scroll-mt-8" data-id="element-124">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-2xl mx-auto text-center space-y-8" data-id="element-125">
          <div className="space-y-4" data-id="element-126">
            <h3 className="text-2xl md:text-3xl font-bold text-text-primary" data-id="element-127">
              No dejes que el colapso de hoy destruya tu semana completa.
            </h3>
            <p className="text-xl text-text-secondary" data-id="element-128">
              Obtén el mapa de emergencia para recuperar tu funcionalidad ahora
              mismo.
            </p>
          </div>

          <div className="py-6 space-y-3" data-id="element-129">
            <div className="inline-block bg-amber-warm/15 text-amber-warm font-heading font-bold text-sm md:text-base px-4 py-1.5 rounded-full tracking-wide" data-id="element-130">
              🔥 68% OFF — Oferta limitada
            </div>
            <div className="flex items-baseline justify-center gap-3" data-id="element-131">
              <span className="text-2xl md:text-3xl text-text-muted line-through decoration-2" data-id="element-132">
                $19.99
              </span>
              <span className="text-5xl md:text-6xl font-bold text-teal" data-id="element-133">
                $6.39 USD
              </span>
            </div>
            <p className="text-text-muted" data-id="element-134">(Pago único. Descarga inmediata.)</p>
          </div>

          <a href="https://cuerposabioo.com/cart/48009920774366:1" target="_blank" rel="noopener noreferrer" className="w-full md:w-auto px-8 py-5 bg-teal hover:bg-teal-400 text-deep font-bold text-lg rounded-lg transition-all transform hover:-translate-y-1 animate-pulse-teal shadow-lg flex items-center justify-center gap-3 mx-auto" data-id="element-135">
            <ShieldIcon className="w-6 h-6" data-id="element-136" />
            DESCARGAR PROTOCOLO P.A.E. AHORA
          </a>

          <div className="flex items-center justify-center gap-2 text-sm text-text-muted mt-6" data-id="element-137">
            <ShieldIcon className="w-4 h-4" data-id="element-138" />
            <p data-id="element-139">
              Tu compra es 100% privada y el cargo aparecerá de forma discreta
              en tu estado de cuenta.
            </p>
          </div>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 px-6 border-t border-white/5" data-id="element-140">
        <div className="max-w-2xl mx-auto flex items-center justify-center gap-6 text-sm text-text-muted" data-id="element-141">
          <button onClick={() => setPrivacyOpen(true)} className="hover:text-teal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/50 rounded" data-id="element-142">
            Política de Privacidad
          </button>
          <span className="w-1 h-1 rounded-full bg-text-muted/40" data-id="element-143" />
          <button onClick={() => setTermsOpen(true)} className="hover:text-teal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/50 rounded" data-id="element-144">
            Términos y Condiciones
          </button>
        </div>
        <p className="text-center text-text-muted/50 text-xs mt-4" data-id="element-145">
          © {new Date().getFullYear()} Protocolo Post-Crisis. Todos los
          derechos reservados.
        </p>
      </footer>

      {/* PRIVACY MODAL */}
      <LegalModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" data-id="element-146">
        <p data-id="element-147">
          <strong data-id="element-148">Última actualización:</strong> Marzo 2026
        </p>
        <p data-id="element-149">
          Tu privacidad es importante para nosotros. Esta política describe cómo
          recopilamos, usamos y protegemos tu información personal.
        </p>
        <h3 className="font-heading font-bold text-text-primary mt-2" data-id="element-150">
          1. Información que recopilamos
        </h3>
        <p data-id="element-151">
          Recopilamos únicamente la información necesaria para procesar tu
          compra: dirección de correo electrónico y datos de pago. No
          almacenamos datos de tarjetas de crédito en nuestros servidores.
        </p>
        <h3 className="font-heading font-bold text-text-primary mt-2" data-id="element-152">
          2. Uso de la información
        </h3>
        <p data-id="element-153">
          Tu información se utiliza exclusivamente para entregar el producto
          digital adquirido y enviarte el enlace de descarga. No compartimos,
          vendemos ni alquilamos tu información personal a terceros.
        </p>
        <h3 className="font-heading font-bold text-text-primary mt-2" data-id="element-154">
          3. Seguridad
        </h3>
        <p data-id="element-155">
          Utilizamos cifrado SSL y procesadores de pago certificados para
          proteger tus datos. El cargo en tu estado de cuenta aparecerá de forma
          discreta.
        </p>
        <h3 className="font-heading font-bold text-text-primary mt-2" data-id="element-156">
          4. Cookies
        </h3>
        <p data-id="element-157">
          Utilizamos cookies mínimas y esenciales para el funcionamiento del
          sitio. No utilizamos cookies de rastreo publicitario.
        </p>
        <h3 className="font-heading font-bold text-text-primary mt-2" data-id="element-158">
          5. Tus derechos
        </h3>
        <p data-id="element-159">
          Puedes solicitar la eliminación de tus datos en cualquier momento.
          Responderemos a tu solicitud en un plazo máximo de 30 días.
        </p>
      </LegalModal>

      {/* TERMS MODAL */}
      <LegalModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos y Condiciones" data-id="element-160">
        <p data-id="element-161">
          <strong data-id="element-162">Última actualización:</strong> Marzo 2026
        </p>
        <p data-id="element-163">
          Al adquirir el Protocolo Post-Crisis, aceptas los siguientes términos
          y condiciones de uso.
        </p>
        <h3 className="font-heading font-bold text-text-primary mt-2" data-id="element-164">
          1. Producto digital
        </h3>
        <p data-id="element-165">
          El Protocolo Post-Crisis es un producto digital de descarga inmediata.
          Una vez completada la compra, recibirás acceso al material en formato
          descargable.
        </p>
        <h3 className="font-heading font-bold text-text-primary mt-2" data-id="element-166">
          2. Uso personal
        </h3>
        <p data-id="element-167">
          El contenido adquirido es para uso personal y no puede ser
          redistribuido, revendido ni compartido públicamente sin autorización
          expresa.
        </p>
        <h3 className="font-heading font-bold text-text-primary mt-2" data-id="element-168">
          3. Descargo de responsabilidad médica
        </h3>
        <p data-id="element-169">
          Este protocolo es un recurso educativo y de apoyo funcional.{' '}
          <strong data-id="element-170">
            No sustituye el diagnóstico, tratamiento ni consejo de un
            profesional de salud mental.
          </strong>{' '}
          Si estás en una situación de emergencia médica, contacta a los
          servicios de emergencia de tu localidad.
        </p>
        <h3 className="font-heading font-bold text-text-primary mt-2" data-id="element-171">
          4. Política de reembolso
        </h3>
        <p data-id="element-172">
          Debido a la naturaleza digital del producto, todas las ventas son
          finales. Si experimentas problemas técnicos con la descarga, te
          ayudaremos a resolverlos.
        </p>
        <h3 className="font-heading font-bold text-text-primary mt-2" data-id="element-173">
          5. Modificaciones
        </h3>
        <p data-id="element-174">
          Nos reservamos el derecho de actualizar estos términos. Los cambios
          serán efectivos desde su publicación en esta página.
        </p>
      </LegalModal>
    </div>;
}